import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Download, Share2, Sparkles } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import type { Components } from "react-markdown";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { SEO } from "@/components/SEO";
import { SiteLayout } from "@/layouts/site-layout";
import { onePagersReduced as onePagerMetaList, OnePagerMeta } from "@/data/one-pagers";
import { cn } from "@/lib/utils";

const MARKDOWN_CLASSNAME =
    "prose prose-slate max-w-none text-base leading-7 prose-headings:font-semibold prose-headings:text-foreground prose-strong:text-foreground prose-a:text-primary prose-a:no-underline hover:prose-a:underline prose-li:marker:text-primary prose-blockquote:border-l-primary/40 prose-blockquote:text-foreground prose-hr:border-border/30 print:prose-headings:text-black print:prose-strong:text-black print:prose-a:text-black";

const markdownComponents: Components = {
    h1: ({ className, ...props }) => (
        <h2 className={cn("mt-10 text-xl font-semibold tracking-tight text-foreground md:text-2xl print:text-black", className)} {...props} />
    ),
    h2: ({ className, ...props }) => (
        <h3 className={cn("mt-8 text-lg font-semibold tracking-tight text-foreground md:text-xl print:text-black", className)} {...props} />
    ),
    h3: ({ className, ...props }) => (
        <h4 className={cn("mt-6 text-base font-semibold uppercase tracking-wide text-muted-foreground print:text-black", className)} {...props} />
    ),
    p: ({ className, ...props }) => (
        <p className={cn("mt-4 text-base leading-7 text-muted-foreground print:text-zinc-700", className)} {...props} />
    ),
    ul: ({ className, ...props }) => (
        <ul className={cn("mt-4 list-disc space-y-2 pl-5 text-muted-foreground print:text-zinc-700", className)} {...props} />
    ),
    ol: ({ className, ...props }) => (
        <ol className={cn("mt-4 list-decimal space-y-2 pl-5 text-muted-foreground print:text-zinc-700", className)} {...props} />
    ),
    li: ({ className, ...props }) => <li className={cn("pl-1", className)} {...props} />,
    strong: ({ className, ...props }) => (
        <strong className={cn("font-semibold text-foreground print:text-black", className)} {...props} />
    ),
    a: ({ className, ...props }) => (
        <a className={cn("font-semibold text-primary underline-offset-4 hover:underline print:text-black", className)} {...props} />
    ),
};

function getRandomGradient(): string {
    const hue1 = Math.floor(Math.random() * 360);
    const hue2 = (hue1 + 60 + Math.floor(Math.random() * 120)) % 360;
    const hue3 = (hue2 + 60 + Math.floor(Math.random() * 120)) % 360;
    const a = 0.25 + Math.random() * 0.25;
    const b = 0.25 + Math.random() * 0.25;
    return `linear-gradient(180deg, hsla(${hue1}, 80%, 60%, ${a}) 0%, hsla(${hue2}, 80%, 60%, ${b}) 50%, hsla(${hue3}, 80%, 60%, ${a}) 80%, hsla(0, 0%, 0%, 0.4) 100%)`;
}

const OnePagerDetail = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [content, setContent] = useState<string>("");
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [hasError, setHasError] = useState<boolean>(false);
    const [retryKey, setRetryKey] = useState<number>(0);

    const activeMeta = useMemo(() => onePagerMetaList.find(item => item.slug === slug), [slug]);
    const headerGradient = useMemo(() => getRandomGradient(), [slug]);

    const { previous, next } = useMemo(() => deriveAdjacentMeta({ items: onePagerMetaList, activeSlug: slug ?? "" }), [slug]);

    useEffect(() => {
        if (!activeMeta) {
            return;
        }

        const controller = new AbortController();
        async function loadMarkdown() {
            try {
                setIsLoading(true);
                setHasError(false);
                const response = await fetch(`/one-pagers/${activeMeta.filename}`, { signal: controller.signal });
                if (!response.ok) {
                    throw new Error(`Failed to load ${activeMeta.filename}`);
                }
                const text = await response.text();
                setContent(text);
            } catch (error) {
                if (!controller.signal.aborted) {
                    setHasError(true);
                }
            } finally {
                if (!controller.signal.aborted) {
                    setIsLoading(false);
                }
            }
        }

        loadMarkdown();

        return () => controller.abort();
    }, [activeMeta, retryKey]);

    if (!activeMeta) {
        return <NotFoundState onNavigateBack={() => navigate("/one-pagers")} />;
    }

    return (
        <SiteLayout>
            <SEO
                title={`${activeMeta.title} | AI One-Pagers`}
                description={activeMeta.description}
                canonicalUrl={`${typeof window !== "undefined" ? window.location.origin : "https://oneweekmvps.com"}/one-pagers/${activeMeta.slug}`}
            />
            <article className="bg-background pb-20 pt-12 text-foreground md:pb-28 print:bg-white print:pb-12 print:pt-10 print:text-black">
                <div className="mx-auto flex max-w-4xl flex-col gap-12 px-4 sm:px-6 lg:px-8 print:max-w-[210mm] print:px-14">
                    <DetailHeader
                        meta={activeMeta}
                        gradient={headerGradient}
                        onNavigateBack={() => navigate("/one-pagers")}
                        onShare={() => handleShare(activeMeta)}
                        onDownload={() => handleDownload(activeMeta)}
                    />

                    <DetailContent
                        content={content}
                        isLoading={isLoading}
                        hasError={hasError}
                        onRetry={() => {
                            setHasError(false);
                            setRetryKey(current => current + 1);
                        }}
                    />

                    <DetailFooter meta={activeMeta} previous={previous} next={next} onNavigate={navigate} />
                </div>
            </article>
        </SiteLayout>
    );
};

export default OnePagerDetail;

function DetailHeader({
    meta,
    gradient,
    onNavigateBack,
    onShare,
    onDownload,
}: DetailHeaderProps) {
    return (
        <header className="relative overflow-hidden rounded-2xl border border-border/60 bg-background/40">
            <div aria-hidden className="absolute inset-x-0 top-0 h-32" style={{ backgroundImage: gradient }} />
            <div className="relative flex flex-col gap-6 p-8 md:p-10">
                <button
                    type="button"
                    onClick={onNavigateBack}
                    className="inline-flex w-fit items-center gap-2 rounded-full border border-transparent bg-muted/40 px-4 py-2 text-sm font-medium text-muted-foreground transition hover:border-muted-foreground/20 hover:bg-muted/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 print:hidden"
                >
                    <ArrowLeft className="h-4 w-4" />
                    Back to library
                </button>

                <div className="flex flex-col gap-4">
                    <h1 className="text-3xl font-semibold tracking-tight text-foreground md:text-4xl lg:text-5xl">{meta.title}</h1>
                    <p className="max-w-3xl text-base text-muted-foreground md:text-lg">{meta.description}</p>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                    <Badge variant="secondary" className="rounded-full bg-muted/60 px-3 py-1 text-xs font-semibold text-muted-foreground">
                        Build budget · {meta.costRange.label}
                    </Badge>
                    <Badge variant="outline" className="rounded-full border-border/60 bg-background/60 px-3 py-1 text-xs text-foreground">
                        Recurring · {meta.monthlyCostRange.label}
                    </Badge>
                    <Badge variant="outline" className="rounded-full border-border/60 bg-background/60 px-3 py-1 text-xs text-foreground">
                        {meta.durationWeeks}-week delivery
                    </Badge>
                </div>

                <div className="mt-4 flex flex-wrap items-center gap-4 print:hidden">
                    <Button className="rounded-xl px-6 py-3 text-base font-semibold" size="lg">
                        <Sparkles className="h-5 w-5" />
                        {meta.heroCta}
                    </Button>
                    <Button variant="outline" className="rounded-xl px-5 py-3" onClick={onShare}>
                        <Share2 className="h-4 w-4" />
                        Share
                    </Button>
                    <Button variant="outline" className="rounded-xl px-5 py-3" onClick={onDownload}>
                        <Download className="h-4 w-4" />
                        Download
                    </Button>
                </div>
            </div>
        </header>
    );
}

function DetailContent({ content, isLoading, hasError, onRetry }: DetailContentProps) {
    return (
        <div className="rounded-2xl border border-border/60 bg-background/80 p-6 print:rounded-none print:border print:border-black/10 print:bg-white print:p-8">
            <div className="mb-8 hidden items-center justify-between border-b border-border pb-6 print:flex print:border-black/20">
                <img src="/assets/logo.png" alt="OneWeekMVPs" className="h-10 w-auto print:h-12" />
                <div className="text-right text-xs font-semibold uppercase tracking-[0.35em] text-muted-foreground print:text-zinc-600">
                    AI One-Pager Playbook
                </div>
            </div>
            {isLoading ? (
                <MarkdownSkeleton />
            ) : hasError ? (
                <ErrorState onRetry={onRetry} />
            ) : (
                <ReactMarkdown className={MARKDOWN_CLASSNAME} remarkPlugins={[remarkGfm]} components={markdownComponents}>
                    {content}
                </ReactMarkdown>
            )}
        </div>
    );
}

function DetailFooter({ meta, previous, next, onNavigate }: DetailFooterProps) {
    return (
        <footer className="flex flex-col gap-6">
            <div className="flex flex-wrap gap-2">
                {meta.tags.map(tag => (
                    <Badge key={tag} variant="outline" className="rounded-full border-border/60 bg-muted/30 px-3 py-1 text-xs">
                        {tag}
                    </Badge>
                ))}
            </div>

            <div className="flex flex-col gap-4 rounded-2xl border border-border/60 bg-muted/10 p-6 transition md:flex-row md:items-center md:justify-between print:border print:border-black/10 print:bg-white">
                <div className="flex flex-col gap-3 md:flex-row md:items-center">
                    {previous ? (
                        <Button variant="ghost" className="justify-start rounded-xl text-left" onClick={() => onNavigate(`/one-pagers/${previous.slug}`)}>
                            <ArrowLeft className="h-4 w-4" />
                            {previous.title}
                        </Button>
                    ) : null}
                </div>
                <div className="flex flex-1 flex-col gap-1 text-sm text-muted-foreground text-center">
                    <p className="font-semibold text-foreground text-center">Keep exploring</p>
                    <p className="text-center">Select another one-pager to compare scope, budget and stack.</p>
                </div>
                <div className="flex flex-col gap-3 md:flex-row md:items-center">
                    {next ? (
                        <Button variant="ghost" className="justify-start rounded-xl text-left" onClick={() => onNavigate(`/one-pagers/${next.slug}`)}>
                            {next.title}
                            <ArrowRight className="h-4 w-4" />
                        </Button>
                    ) : null}
                </div>
            </div>
        </footer>
    );
}

function NotFoundState({ onNavigateBack }: NotFoundStateProps) {
    return (
        <SiteLayout>
            <section className="flex min-h-[60vh] flex-col items-center justify-center gap-6 px-4 text-center">
                <h1 className="text-3xl font-semibold text-foreground md:text-4xl">We could not find that one-pager.</h1>
                <p className="max-w-xl text-muted-foreground">
                    Double-check the URL or head back to the library to explore the full catalogue of AI launch plans.
                </p>
                <Button onClick={onNavigateBack} className="rounded-xl">
                    Browse one-pagers
                </Button>
            </section>
        </SiteLayout>
    );
}

function MarkdownSkeleton() {
    return (
        <div className="space-y-4">
            <Skeleton className="h-8 w-3/4 rounded-lg" />
            <Skeleton className="h-4 w-full rounded-lg" />
            <Skeleton className="h-4 w-full rounded-lg" />
            <Skeleton className="h-4 w-5/6 rounded-lg" />
            <Skeleton className="h-6 w-2/3 rounded-lg" />
            <Skeleton className="h-4 w-full rounded-lg" />
            <Skeleton className="h-4 w-full rounded-lg" />
            <Skeleton className="h-4 w-4/5 rounded-lg" />
        </div>
    );
}

function ErrorState({ onRetry }: ErrorStateProps) {
    return (
        <div className="flex flex-col items-center justify-center gap-4 text-center">
            <p className="text-sm font-medium text-muted-foreground">We couldn&apos;t load this one-pager right now.</p>
            <Button variant="outline" className="rounded-xl" onClick={onRetry}>
                Retry
            </Button>
        </div>
    );
}

function handleShare(meta: OnePagerMeta) {
    const shareUrl = `${window.location.origin}/one-pagers/${meta.slug}`;
    if (navigator.share) {
        navigator
            .share({
                title: meta.title,
                text: meta.description,
                url: shareUrl,
            })
            .catch(() => undefined);
        return;
    }
    void navigator.clipboard.writeText(shareUrl);
}

function handleDownload(meta: OnePagerMeta) {
    const pdfUrl = `/one-pagers/${meta.slug}/pdf`;
    window.open(pdfUrl, "_blank", "noopener,noreferrer");
}

function deriveAdjacentMeta({ items, activeSlug }: DeriveAdjacentMetaParams) {
    if (!activeSlug) return { previous: undefined, next: undefined };
    const index = items.findIndex(item => item.slug === activeSlug);
    if (index === -1) return { previous: undefined, next: undefined };
    const previous = items[index - 1];
    const next = items[index + 1];
    return { previous, next };
}

interface DetailHeaderProps {
    meta: OnePagerMeta;
    gradient: string;
    onNavigateBack: () => void;
    onShare: () => void;
    onDownload: () => void;
}

interface DetailContentProps {
    content: string;
    isLoading: boolean;
    hasError: boolean;
    onRetry: () => void;
}

interface DetailFooterProps {
    meta: OnePagerMeta;
    previous?: OnePagerMeta;
    next?: OnePagerMeta;
    onNavigate: (path: string) => void;
}

interface NotFoundStateProps {
    onNavigateBack: () => void;
}

interface DeriveAdjacentMetaParams {
    items: OnePagerMeta[];
    activeSlug: string;
}

interface ErrorStateProps {
    onRetry: () => void;
}
